import * as Sentry from '@sentry/react';
import { THEME_COLOR } from '@shared/design/theme';
import type { Platform } from '@shared/platform';
import type { Locale } from '@shared/stores/localeStore';
import { logger } from '@/utils/logger';

/**
 * Платформа веба и Electron-рендерера. Каждое обращение к window / document /
 * localStorage — в момент вызова, а не при создании: тестовый setup
 * подменяет эти глобалы, а jsdom-файлы приносят свои.
 */
export function createWebPlatform(): Platform {
  return {
    storage: {
      getItem: (key) => localStorage.getItem(key),
      setItem: (key, value) => localStorage.setItem(key, value),
      removeItem: (key) => localStorage.removeItem(key),
      onExternalChange: (listener) => {
        const handler = (event: Event) => listener((event as StorageEvent).key);
        window.addEventListener('storage', handler);
        return () => window.removeEventListener('storage', handler);
      },
    },
    sessionStorage: {
      getItem: (key) => window.sessionStorage.getItem(key),
      setItem: (key, value) => window.sessionStorage.setItem(key, value),
      removeItem: (key) => window.sessionStorage.removeItem(key),
      // Событие storage для sessionStorage между вкладками не приходит.
      onExternalChange: () => () => {},
    },
    env: {
      get apiUrl() {
        return import.meta.env.VITE_API_URL || 'http://localhost:8080';
      },
      get wsUrl() {
        return import.meta.env.VITE_WS_URL || 'ws://localhost:8080';
      },
      get sfuUrl() {
        return import.meta.env.VITE_SFU_URL || 'ws://localhost:8081';
      },
      get appVersion() {
        return __APP_VERSION__;
      },
      get clientPlatform() {
        const p = typeof window !== 'undefined' ? window.electronAPI?.platform : undefined;
        return p ? `electron-${p}` : 'web';
      },
      get publicWebUrl() {
        const configured = import.meta.env.VITE_PUBLIC_WEB_URL;
        return configured && configured.length > 0 ? configured : window.location.origin;
      },
    },
    lifecycle: {
      onForeground: (listener) => {
        const handler = () => {
          if (document.visibilityState === 'visible') listener();
        };
        document.addEventListener('visibilitychange', handler);
        return () => document.removeEventListener('visibilitychange', handler);
      },
      isFocused: () => document.hasFocus() && document.visibilityState === 'visible',
      onFocusChange: (listener) => {
        const handler = () => listener(document.hasFocus() && document.visibilityState === 'visible');
        window.addEventListener('focus', handler);
        window.addEventListener('blur', handler);
        document.addEventListener('visibilitychange', handler);
        return () => {
          window.removeEventListener('focus', handler);
          window.removeEventListener('blur', handler);
          document.removeEventListener('visibilitychange', handler);
        };
      },
      onUserActivity: (listener) => {
        // Не чаще раза в секунду: pointermove/wheel сыплются сотнями в секунду.
        let lastAt = 0;
        const handler = () => {
          const now = Date.now();
          if (now - lastAt < 1000) return;
          lastAt = now;
          listener();
        };
        const events = ['keydown', 'pointerdown', 'pointermove', 'wheel', 'touchstart'] as const;
        events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
        return () => events.forEach((e) => window.removeEventListener(e, handler));
      },
    },
    telemetry: {
      error: (message, err, tags) => logger.error(message, err, tags),
      breadcrumb: (category, data, level) => logger.breadcrumb(category, data, level),
      report: (message, tags, extra, level) => logger.report(message, tags, extra, level),
      setTag: (key, value) => Sentry.setTag(key, value),
    },
    applyLocale: (locale) => {
      document.documentElement.setAttribute('lang', locale);
      // Меню трея живёт в main-процессе Electron и о сторе не знает.
      // В браузерной версии electronAPI нет — тихо пропускаем.
      window.electronAPI?.setLocale?.(locale as Locale);
    },
    applyTheme: (theme) => {
      document.documentElement.setAttribute('data-theme', theme);
      window.electronAPI?.setTheme?.(theme);
      // Тема приложения может расходиться с системной: переписываем оба
      // media-варианта, иначе системная тема перекрасила бы статус-бар PWA.
      document.querySelectorAll?.('meta[name="theme-color"]').forEach((m) => m.setAttribute('content', THEME_COLOR[theme]));
    },
    prefersDarkScheme: () => window.matchMedia('(prefers-color-scheme: dark)').matches,
  };
}
